import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { alpha } from "@mui/material/styles";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs, { type Dayjs } from "dayjs";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import DatePickerComponent, {
	type DatePickerType,
} from "../../../components/DatePickerComponent";
import ProgressDialog from "../../../components/ProgressDialog";
import TableComponent from "../../../components/TableComponent";
import { AppContext } from "../../../context/AppContextProvider";
import useJobProgress from "../../../hooks/useJobProgress";
import { getAllGeneralByLicenseAsync } from "../../../services/Recognition/General";
import type { IGeneralRecognitionSimpleProps } from "../../../services/Recognition/General/types";
import { getAllResponsibleByLicenseAsync } from "../../../services/Recognition/Responsible";
import type { IResponsibleRecognitionSimpleProps } from "../../../services/Recognition/Responsible/types";
import { createAsyncReport } from "../../../services/ReportService";
import type { IAsyncReportCreateRequest } from "../../../services/ReportService/types";
import { getAllSchoolClassByLicenseAsync } from "../../../services/SchoolClass";
import { getAllStudentByLicenseAsync } from "../../../services/StudentService";
import type { IStudentSimpleProps } from "../../../services/StudentService/types";
import type { IPaginationParams } from "../../../services/types";

type SchoolClassOption = {
	id: number;
	name: string;
};

type EventEntityTypeOption =
	| "student"
	| "responsible"
	| "general"
	| "unregistered";

type PersonEntityType = Exclude<EventEntityTypeOption, "unregistered">;

type PersonSearchOption = {
	id: number;
	name: string;
	cpf?: string;
	registration?: string;
	entityType: PersonEntityType;
};

const toRecognitionOption = (
	item: IResponsibleRecognitionSimpleProps | IGeneralRecognitionSimpleProps,
	entityType: Exclude<PersonEntityType, "student">,
): PersonSearchOption | null => {
	if (typeof item?.id !== "number" || !item?.name) {
		return null;
	}

	return {
		id: item.id,
		name: item.name,
		cpf: item.cpf,
		entityType,
	};
};

const toStudentPersonOption = (
	student: IStudentSimpleProps,
): PersonSearchOption => ({
	id: student.id,
	name: student.name,
	cpf: student.cpf,
	registration: student.registration,
	entityType: "student",
});

type ExitEntityTypeOption = "student" | "responsible" | "general";

type ReportRow = {
	id: number;
	reportName: string;
	prefix: string;
	periodType?: DatePickerType;
	group: (
		| "Alunos"
		| "Eventos"
		| "Funcionários"
		| "Responsáveis"
		| "Reconhecimento"
		| "Outros"
	)[];
	supportsIncludeImages: boolean;
	requiresStudent?: boolean;
	requiresClasses?: boolean;
	supportsStudentFilter?: boolean;
	supportsClassesFilter?: boolean;
	supportsEventEntityTypeFilter?: boolean;
	supportsDiscriminatorFilter?: boolean;
	supportsExitEntityTypeFilter?: boolean;
	supportsIncludePersonTypeOption?: boolean;
	supportsIncludeDeviceOption?: boolean;
	supportsIncludeIpOption?: boolean;
	supportsIncludeSchoolClassOption?: boolean;
	includeSchoolClassDefault?: boolean;
	allowEmptyStudentSelection?: boolean;
	supportsIncludeCreatedAtOption?: boolean;
	supportsIncludeResponsibleUserOption?: boolean;
	supportsOptionalPeriod?: boolean;
	isAttendanceReport?: boolean;
	requiresPeriod?: boolean;
	usesLast30DaysPeriod?: boolean;
	asyncReportType: string;
};

type AttendanceFormState = {
	entryWindowStart: string;
	entryWindowEnd: string;
	toleranceBeforeMinutes: number;
	toleranceAfterMinutes: number;
	minimumExitTime: string;
	evaluationMode: "entry_and_exit" | "only_entry";
};

const ATTENDANCE_DEFAULTS: AttendanceFormState = {
	entryWindowStart: "07:00",
	entryWindowEnd: "08:00",
	toleranceBeforeMinutes: 10,
	toleranceAfterMinutes: 15,
	minimumExitTime: "11:30",
	evaluationMode: "entry_and_exit",
};

type ReportRowView = ReportRow & { sortOrder: number };

type GroupTone =
	| "primary"
	| "warning"
	| "success"
	| "info"
	| "secondary"
	| "neutral";

const ReportTable = () => {
	const { showSnackbar } = useContext(AppContext);

	const reports = useMemo<ReportRow[]>(
		() => [
			// Alunos
			{
				id: 1,
				reportName: "Alunos - Cadastro",
				prefix: "alunos_cadastro",
				group: ["Alunos"],
				supportsIncludeImages: true,
				asyncReportType: "student_register",
			},

			{
				id: 17,
				reportName: "Saídas - Tipos",
				prefix: "saidas",
				periodType: "datetime",
				group: ["Eventos", "Alunos", "Responsáveis", "Funcionários"],
				supportsIncludeImages: true,
				supportsExitEntityTypeFilter: true,
				supportsIncludePersonTypeOption: true,
				asyncReportType: "exit_events",
			},
			{
				id: 4,
				reportName: "Alunos - Sem Eventos",
				prefix: "alunos_sem_eventos",
				periodType: "datetime",
				group: ["Alunos", "Eventos"],
				supportsIncludeImages: true,
				supportsClassesFilter: true,
				supportsIncludeSchoolClassOption: true,
				includeSchoolClassDefault: true,
				asyncReportType: "students_without_events",
			},
			{
				id: 13,
				reportName: "Alunos - Movimentações (30 dias)",
				prefix: "alunos_movimentacoes_30",
				group: ["Alunos", "Eventos"],
				supportsIncludeImages: true,
				supportsIncludeDeviceOption: true,
				requiresStudent: true,
				allowEmptyStudentSelection: true,
				usesLast30DaysPeriod: true,
				asyncReportType: "student_entries_last30_days",
			},
			{
				id: 14,
				reportName: "Alunos - Turmas",
				prefix: "alunos_turmas",
				group: ["Alunos"],
				supportsIncludeImages: true,
				requiresClasses: true,
				requiresPeriod: false,
				asyncReportType: "students_by_class",
			},
			{
				id: 15,
				reportName: "Alunos - Presença",
				prefix: "alunos_presenca",
				group: ["Alunos", "Eventos"],
				supportsIncludeImages: true,
				supportsStudentFilter: true,
				supportsClassesFilter: true,
				isAttendanceReport: true,
				asyncReportType: "student_attendance",
			},

			// Eventos gerais
			{
				id: 5,
				reportName: "Eventos - Tipos",
				prefix: "eventos",
				periodType: "datetime",
				group: ["Eventos", "Alunos", "Responsáveis", "Funcionários"],
				supportsIncludeImages: true,
				supportsEventEntityTypeFilter: true,
				supportsIncludeDeviceOption: true,
				supportsStudentFilter: true,
				supportsClassesFilter: true,
				supportsIncludeSchoolClassOption: true,
				asyncReportType: "student_events",
			},

			// Reconhecimento
			{
				id: 16,
				reportName: "Reconhecimento - Auditoria de Atualizações",
				prefix: "reconhecimento_atualizacoes",
				periodType: "datetime",
				group: ["Reconhecimento"],
				supportsIncludeImages: true,
				supportsDiscriminatorFilter: true,
				supportsIncludeCreatedAtOption: true,
				supportsIncludeResponsibleUserOption: true,
				asyncReportType: "recognition_updates_audit",
			},

			// Outros
			{
				id: 10,
				reportName: "Dispositivos",
				prefix: "outros_dispositivos",
				group: ["Outros"],
				supportsIncludeImages: false,
				supportsIncludeIpOption: true,
				supportsOptionalPeriod: true,
				asyncReportType: "device",
			},
			{
				id: 11,
				reportName: "Rostos - Geral",
				prefix: "outros_rostos",
				group: ["Outros"],
				supportsIncludeImages: true,
				supportsDiscriminatorFilter: true,
				supportsOptionalPeriod: true,
				asyncReportType: "faces",
			},
		],
		[],
	);

	const groupColorMap: Record<ReportRow["group"][number], GroupTone> = {
		Alunos: "primary",
		Eventos: "warning",
		Funcionários: "success",
		Responsáveis: "info",
		Reconhecimento: "secondary",
		Outros: "neutral",
	};

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [selectedReport, setSelectedReport] = useState<number | null>(null);
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [type, setType] = useState("pdf");
	const [includeImages, setIncludeImages] = useState(true);
	const [includePersonType, setIncludePersonType] = useState(true);
	const [includeDevice, setIncludeDevice] = useState(true);
	const [includeIp, setIncludeIp] = useState(true);
	const [includeSchoolClass, setIncludeSchoolClass] = useState(false);
	const [includeCreatedAt, setIncludeCreatedAt] = useState(true);
	const [includeResponsibleUser, setIncludeResponsibleUser] = useState(true);
	const [usePeriod, setUsePeriod] = useState(true);
	const [recognitionTypeFilters, setRecognitionTypeFilters] = useState<
		string[]
	>([]);
	const [eventEntityTypeFilters, setEventEntityTypeFilters] = useState<
		EventEntityTypeOption[]
	>([]);
	const [exitEntityTypeFilters, setExitEntityTypeFilters] = useState<
		ExitEntityTypeOption[]
	>([]);
	const [studentQuery, setStudentQuery] = useState("");
	const [selectedStudent, setSelectedStudent] =
		useState<IStudentSimpleProps | null>(null);
	const [selectedStudents, setSelectedStudents] = useState<
		IStudentSimpleProps[]
	>([]);
	const [selectedPeople, setSelectedPeople] = useState<PersonSearchOption[]>(
		[],
	);
	const [attendanceForm, setAttendanceForm] =
		useState<AttendanceFormState>(ATTENDANCE_DEFAULTS);
	const [studentOptions, setStudentOptions] = useState<IStudentSimpleProps[]>(
		[],
	);
	const [responsibleOptions, setResponsibleOptions] = useState<
		PersonSearchOption[]
	>([]);
	const [generalOptions, setGeneralOptions] = useState<PersonSearchOption[]>(
		[],
	);
	const [classOptions, setClassOptions] = useState<SchoolClassOption[]>([]);
	const [selectedClasses, setSelectedClasses] = useState<SchoolClassOption[]>(
		[],
	);
	const [isClassLoading, setIsClassLoading] = useState(false);
	const [isStudentLoading, setIsStudentLoading] = useState(false);
	const [isResponsibleLoading, setIsResponsibleLoading] = useState(false);
	const [isGeneralLoading, setIsGeneralLoading] = useState(false);
	const [reportNameFilter, setReportNameFilter] = useState("");
	const [selectedGroupFilters, setSelectedGroupFilters] = useState<
		ReportRow["group"][number][]
	>([]);
	const [periodErrorMessage, setPeriodErrorMessage] = useState("");
	const [studentErrorMessage, setStudentErrorMessage] = useState("");
	const [classErrorMessage, setClassErrorMessage] = useState("");
	const [attendanceErrorMessage, setAttendanceErrorMessage] = useState("");
	const [pagination, setPagination] = useState<IPaginationParams>({
		pageNumber: 1,
		pageSize: 10,
	});

	const selectedReportMeta = useMemo(
		() =>
			selectedReport ? reports.find((r) => r.id === selectedReport) : undefined,
		[selectedReport, reports],
	);
	const selectedPeriodType: DatePickerType =
		selectedReportMeta?.periodType ?? "date";

	const availableGroups = useMemo(
		() =>
			Array.from(new Set(reports.flatMap((report) => report.group))).sort(
				(a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }),
			),
		[reports],
	);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const jobProgress = useJobProgress();
	const activeReportNameRef = useRef<string | null>(null);

	const hasShownStudentLoadErrorRef = useRef(false);
	const hasShownClassLoadErrorRef = useRef(false);
	const hasShownResponsibleLoadErrorRef = useRef(false);
	const hasShownGeneralLoadErrorRef = useRef(false);
	const PEOPLE_FILTER_PAGE_SIZE = 50;

	// O parser do backend interpreta entradas sem offset como UTC.
	// Como os timestamps do domínio estão sendo tratados no "clock local" (GMT-3),
	// enviamos o período sem conversão automática de fuso para evitar deslocamento de 3h.
	const serializeDateTimeForApi = (value: Dayjs) =>
		value.format("YYYY-MM-DDTHH:mm:ss");

	const getSuggestedPeriodRange = () => {
		const now = dayjs();
		return {
			start: serializeDateTimeForApi(now.startOf("day")),
			end: serializeDateTimeForApi(
				now.hour(23).minute(59).second(0).millisecond(0),
			),
		};
	};

	const formatCpf = (cpf?: string) => {
		const digits = (cpf ?? "").replace(/\D/g, "");
		if (digits.length !== 11) return cpf ?? "";
		return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
	};

	const getStudentIdentifier = (student: IStudentSimpleProps) => {
		const registration = student.registration?.trim();
		const cpf = student.cpf?.trim();
		const formattedCpf = cpf ? formatCpf(cpf) : "";

		if (registration && formattedCpf) {
			return `Matrícula ${registration} • CPF ${formattedCpf}`;
		}

		if (registration) {
			return `Matrícula ${registration}`;
		}

		if (formattedCpf) {
			return `CPF ${formattedCpf}`;
		}

		return "Matrícula/CPF não informados";
	};

	const formatEntityTypeLabel = (entityType: PersonEntityType) => {
		const labels: Record<PersonEntityType, string> = {
			student: "Estudante",
			responsible: "Responsável",
			general: "Funcionário",
		};

		return labels[entityType];
	};

	const getPersonIdentifier = (option: PersonSearchOption) => {
		const formattedCpf = option.cpf ? formatCpf(option.cpf) : "";

		if (option.entityType === "student") {
			if (option.registration && formattedCpf) {
				return `Matrícula ${option.registration} • CPF ${formattedCpf}`;
			}

			if (option.registration) {
				return `Matrícula ${option.registration}`;
			}
		}

		if (formattedCpf) {
			return `CPF ${formattedCpf}`;
		}

		return formatEntityTypeLabel(option.entityType);
	};

	const filterPersonOptionsByQuery = (
		options: PersonSearchOption[],
		query: string,
	) => {
		const normalizedQuery = query.trim().toLowerCase();
		if (!normalizedQuery) return options;

		return options.filter((option) => {
			const name = option.name?.toLowerCase() ?? "";
			const cpf = option.cpf?.toLowerCase() ?? "";
			const registration = option.registration?.toLowerCase() ?? "";
			const entityTypeLabel = formatEntityTypeLabel(
				option.entityType,
			).toLowerCase();

			return (
				name.includes(normalizedQuery) ||
				cpf.includes(normalizedQuery) ||
				registration.includes(normalizedQuery) ||
				entityTypeLabel.includes(normalizedQuery)
			);
		});
	};

	const filterStudentOptionsByQuery = (
		options: IStudentSimpleProps[],
		query: string,
	) => {
		const normalizedQuery = query.trim().toLowerCase();
		if (!normalizedQuery) return options;

		return options.filter((student) => {
			const name = student.name?.toLowerCase() ?? "";
			const registration = student.registration?.toLowerCase() ?? "";
			const cpf = student.cpf?.toLowerCase() ?? "";
			return (
				name.includes(normalizedQuery) ||
				registration.includes(normalizedQuery) ||
				cpf.includes(normalizedQuery)
			);
		});
	};

	const requiresStudent = selectedReportMeta?.requiresStudent ?? false;
	const allowEmptyStudentSelection =
		selectedReportMeta?.allowEmptyStudentSelection ?? false;
	const requiresClasses = selectedReportMeta?.requiresClasses ?? false;
	const supportsStudentFilter =
		selectedReportMeta?.supportsStudentFilter ?? false;
	const supportsClassesFilter =
		selectedReportMeta?.supportsClassesFilter ?? false;
	const supportsDiscriminatorFilter =
		selectedReportMeta?.supportsDiscriminatorFilter ?? false;
	const supportsEventEntityTypeFilter =
		selectedReportMeta?.supportsEventEntityTypeFilter ?? false;
	const supportsExitEntityTypeFilter =
		selectedReportMeta?.supportsExitEntityTypeFilter ?? false;
	const supportsIncludePersonTypeOption =
		selectedReportMeta?.supportsIncludePersonTypeOption ?? false;
	const supportsIncludeDeviceOption =
		selectedReportMeta?.supportsIncludeDeviceOption ?? false;
	const supportsIncludeIpOption =
		selectedReportMeta?.supportsIncludeIpOption ?? false;
	const supportsIncludeSchoolClassOption =
		selectedReportMeta?.supportsIncludeSchoolClassOption ?? false;
	const hasStudentSelectedInEventFilter =
		!supportsEventEntityTypeFilter ||
		eventEntityTypeFilters.length === 0 ||
		eventEntityTypeFilters.includes("student");
	const hasResponsibleSelectedInEventFilter =
		!supportsEventEntityTypeFilter ||
		eventEntityTypeFilters.length === 0 ||
		eventEntityTypeFilters.includes("responsible");
	const hasGeneralSelectedInEventFilter =
		!supportsEventEntityTypeFilter ||
		eventEntityTypeFilters.length === 0 ||
		eventEntityTypeFilters.includes("general");
	const shouldShowStudentFilter =
		supportsStudentFilter && hasStudentSelectedInEventFilter;
	const shouldShowStudentOnlyFilter =
		shouldShowStudentFilter && !supportsEventEntityTypeFilter;
	const shouldShowResponsibleFilter =
		supportsEventEntityTypeFilter && hasResponsibleSelectedInEventFilter;
	const shouldShowGeneralFilter =
		supportsEventEntityTypeFilter && hasGeneralSelectedInEventFilter;
	const shouldShowUnifiedPeopleFilter =
		supportsEventEntityTypeFilter &&
		(hasStudentSelectedInEventFilter ||
			hasResponsibleSelectedInEventFilter ||
			hasGeneralSelectedInEventFilter);
	const shouldShowClassesFilter =
		(requiresClasses || supportsClassesFilter) &&
		hasStudentSelectedInEventFilter;
	const shouldShowIncludeSchoolClassOption =
		supportsIncludeSchoolClassOption && hasStudentSelectedInEventFilter;
	const supportsIncludeCreatedAtOption =
		selectedReportMeta?.supportsIncludeCreatedAtOption ?? false;
	const supportsIncludeResponsibleUserOption =
		selectedReportMeta?.supportsIncludeResponsibleUserOption ?? false;
	const supportsIncludeImagesOption =
		selectedReportMeta?.supportsIncludeImages ?? false;
	const hasAnyColumnOption =
		supportsIncludeImagesOption ||
		supportsIncludePersonTypeOption ||
		supportsIncludeDeviceOption ||
		supportsIncludeIpOption ||
		shouldShowIncludeSchoolClassOption ||
		supportsIncludeCreatedAtOption ||
		supportsIncludeResponsibleUserOption;
	const isAttendanceReport = selectedReportMeta?.isAttendanceReport ?? false;
	const supportsOptionalPeriod =
		selectedReportMeta?.supportsOptionalPeriod ?? false;
	const requiresPeriod =
		(selectedReportMeta?.requiresPeriod ?? true) &&
		(!supportsOptionalPeriod || usePeriod);
	const usesLast30DaysPeriod =
		selectedReportMeta?.usesLast30DaysPeriod ?? false;
	const shouldLoadStudents = requiresStudent || shouldShowStudentFilter;
	const shouldLoadClasses = requiresClasses || shouldShowClassesFilter;

	const peopleOptions = useMemo(() => {
		const options: PersonSearchOption[] = [];

		if (hasStudentSelectedInEventFilter) {
			options.push(...studentOptions.map(toStudentPersonOption));
		}

		if (hasResponsibleSelectedInEventFilter) {
			options.push(...responsibleOptions);
		}

		if (hasGeneralSelectedInEventFilter) {
			options.push(...generalOptions);
		}

		return options.sort((a, b) =>
			a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }),
		);
	}, [
		generalOptions,
		hasGeneralSelectedInEventFilter,
		hasResponsibleSelectedInEventFilter,
		hasStudentSelectedInEventFilter,
		responsibleOptions,
		studentOptions,
	]);

	const isPeopleLoading =
		(hasStudentSelectedInEventFilter && isStudentLoading) ||
		(hasResponsibleSelectedInEventFilter && isResponsibleLoading) ||
		(hasGeneralSelectedInEventFilter && isGeneralLoading);

	useEffect(() => {
		if (!hasStudentSelectedInEventFilter) {
			setSelectedClasses([]);
			setIncludeSchoolClass(false);
		}

		if (!supportsEventEntityTypeFilter) return;

		setSelectedPeople((previous) =>
			previous.filter((person) => {
				if (person.entityType === "student") {
					return hasStudentSelectedInEventFilter;
				}

				if (person.entityType === "responsible") {
					return hasResponsibleSelectedInEventFilter;
				}

				return hasGeneralSelectedInEventFilter;
			}),
		);
	}, [
		hasGeneralSelectedInEventFilter,
		hasResponsibleSelectedInEventFilter,
		hasStudentSelectedInEventFilter,
		supportsEventEntityTypeFilter,
	]);

	useEffect(() => {
		if (!isDialogOpen || !shouldLoadStudents) {
			setStudentOptions([]);
			setIsStudentLoading(false);
			return;
		}

		let isCancelled = false;
		const timer = setTimeout(async () => {
			setIsStudentLoading(true);
			try {
				const normalizedQuery = studentQuery.trim().toLowerCase();

				const students = await getAllStudentByLicenseAsync(
					{ pageNumber: 1, pageSize: 25 },
					{ search: studentQuery.trim() },
				);

				let finalOptions = students;

				if (normalizedQuery && students.length === 0) {
					const fallbackStudents = await getAllStudentByLicenseAsync(
						{ pageNumber: 1, pageSize: 200 },
						{ search: "" },
					);

					finalOptions = fallbackStudents
						.filter((student) => {
							const name = student.name?.toLowerCase() ?? "";
							const registration = student.registration?.toLowerCase() ?? "";
							const cpf = student.cpf?.toLowerCase() ?? "";
							return (
								name.includes(normalizedQuery) ||
								registration.includes(normalizedQuery) ||
								cpf.includes(normalizedQuery)
							);
						})
						.slice(0, 25);
				}

				if (!isCancelled) {
					setStudentOptions(finalOptions);
					hasShownStudentLoadErrorRef.current = false;
				}
			} catch {
				if (!isCancelled) {
					setStudentOptions([]);
					if (!hasShownStudentLoadErrorRef.current) {
						hasShownStudentLoadErrorRef.current = true;
						showSnackbar(
							"Não foi possível carregar os alunos para filtro. Tente novamente.",
							"error",
						);
					}
				}
			} finally {
				if (!isCancelled) {
					setIsStudentLoading(false);
				}
			}
		}, 350);

		return () => {
			isCancelled = true;
			clearTimeout(timer);
		};
	}, [isDialogOpen, shouldLoadStudents, studentQuery, showSnackbar]);

	useEffect(() => {
		if (!isDialogOpen || !shouldLoadClasses) {
			setClassOptions([]);
			setIsClassLoading(false);
			return;
		}

		let isCancelled = false;

		const loadClasses = async () => {
			setIsClassLoading(true);
			try {
				const classes = await getAllSchoolClassByLicenseAsync();
				if (!isCancelled) {
					setClassOptions(
						(classes ?? [])
							.filter(
								(item): item is SchoolClassOption =>
									typeof item?.id === "number" && !!item?.name,
							)
							.sort((a, b) =>
								a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }),
							),
					);
					hasShownClassLoadErrorRef.current = false;
				}
			} catch {
				if (!isCancelled) {
					setClassOptions([]);
					if (!hasShownClassLoadErrorRef.current) {
						hasShownClassLoadErrorRef.current = true;
						showSnackbar(
							"Não foi possível carregar as turmas para filtro. Tente novamente.",
							"error",
						);
					}
				}
			} finally {
				if (!isCancelled) {
					setIsClassLoading(false);
				}
			}
		};

		loadClasses();

		return () => {
			isCancelled = true;
		};
	}, [isDialogOpen, shouldLoadClasses, showSnackbar]);

	useEffect(() => {
		if (!isDialogOpen || !shouldShowResponsibleFilter) {
			setResponsibleOptions([]);
			setIsResponsibleLoading(false);
			return;
		}

		let isCancelled = false;
		const timer = setTimeout(async () => {
			setIsResponsibleLoading(true);
			try {
				const responsibles = await getAllResponsibleByLicenseAsync(
					{ pageNumber: 1, pageSize: PEOPLE_FILTER_PAGE_SIZE },
					{ search: studentQuery.trim() || undefined },
				);

				if (!isCancelled) {
					setResponsibleOptions(
						(responsibles ?? [])
							.map((item) => toRecognitionOption(item, "responsible"))
							.filter((item): item is PersonSearchOption => item !== null)
							.sort((a, b) =>
								a.name.localeCompare(b.name, "pt-BR", {
									sensitivity: "base",
								}),
							),
					);
					hasShownResponsibleLoadErrorRef.current = false;
				}
			} catch {
				if (!isCancelled) {
					setResponsibleOptions([]);
					if (!hasShownResponsibleLoadErrorRef.current) {
						hasShownResponsibleLoadErrorRef.current = true;
						showSnackbar(
							"Não foi possível carregar os responsáveis para filtro. Tente novamente.",
							"error",
						);
					}
				}
			} finally {
				if (!isCancelled) {
					setIsResponsibleLoading(false);
				}
			}
		}, 300);

		return () => {
			isCancelled = true;
			clearTimeout(timer);
		};
	}, [isDialogOpen, shouldShowResponsibleFilter, showSnackbar, studentQuery]);

	useEffect(() => {
		if (!isDialogOpen || !shouldShowGeneralFilter) {
			setGeneralOptions([]);
			setIsGeneralLoading(false);
			return;
		}

		let isCancelled = false;
		const timer = setTimeout(async () => {
			setIsGeneralLoading(true);
			try {
				const generals = await getAllGeneralByLicenseAsync(
					{ pageNumber: 1, pageSize: PEOPLE_FILTER_PAGE_SIZE },
					{ search: studentQuery.trim() || undefined },
				);

				if (!isCancelled) {
					setGeneralOptions(
						(generals ?? [])
							.map((item) => toRecognitionOption(item, "general"))
							.filter((item): item is PersonSearchOption => item !== null)
							.sort((a, b) =>
								a.name.localeCompare(b.name, "pt-BR", {
									sensitivity: "base",
								}),
							),
					);
					hasShownGeneralLoadErrorRef.current = false;
				}
			} catch {
				if (!isCancelled) {
					setGeneralOptions([]);
					if (!hasShownGeneralLoadErrorRef.current) {
						hasShownGeneralLoadErrorRef.current = true;
						showSnackbar(
							"Não foi possível carregar os funcionários para filtro. Tente novamente.",
							"error",
						);
					}
				}
			} finally {
				if (!isCancelled) {
					setIsGeneralLoading(false);
				}
			}
		}, 300);

		return () => {
			isCancelled = true;
			clearTimeout(timer);
		};
	}, [isDialogOpen, shouldShowGeneralFilter, showSnackbar, studentQuery]);

	const handleOpenDialog = (report: number) => {
		const reportMeta = reports.find((item) => item.id === report);
		const requiresPeriodByReport = reportMeta?.requiresPeriod ?? true;

		setSelectedReport(report);
		setType("pdf");
		setIncludeImages(true);
		setIncludePersonType(true);
		setIncludeDevice(true);
		setIncludeIp(true);
		setIncludeSchoolClass(reportMeta?.includeSchoolClassDefault ?? false);
		setIncludeCreatedAt(true);
		setIncludeResponsibleUser(true);
		setUsePeriod(true);
		setRecognitionTypeFilters([]);
		setEventEntityTypeFilters([]);
		setExitEntityTypeFilters([]);
		setStudentQuery("");
		setSelectedStudent(null);
		setSelectedStudents([]);
		setSelectedPeople([]);
		setSelectedClasses([]);
		setResponsibleOptions([]);
		setGeneralOptions([]);
		setAttendanceForm(ATTENDANCE_DEFAULTS);
		setPeriodErrorMessage("");
		setStudentErrorMessage("");
		setClassErrorMessage("");
		setAttendanceErrorMessage("");
		hasShownStudentLoadErrorRef.current = false;
		hasShownClassLoadErrorRef.current = false;
		hasShownResponsibleLoadErrorRef.current = false;
		hasShownGeneralLoadErrorRef.current = false;

		if (requiresPeriodByReport && !reportMeta?.usesLast30DaysPeriod) {
			const suggestedPeriod = getSuggestedPeriodRange();
			setStartDate(suggestedPeriod.start);
			setEndDate(suggestedPeriod.end);
		} else {
			setStartDate("");
			setEndDate("");
		}

		setIsDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setIsDialogOpen(false);
		setSelectedReport(null);
		setStartDate("");
		setEndDate("");
		setType("pdf");
		setIncludeImages(true);
		setIncludePersonType(true);
		setIncludeDevice(true);
		setIncludeIp(true);
		setIncludeSchoolClass(false);
		setIncludeCreatedAt(true);
		setIncludeResponsibleUser(true);
		setUsePeriod(true);
		setRecognitionTypeFilters([]);
		setEventEntityTypeFilters([]);
		setExitEntityTypeFilters([]);
		setStudentQuery("");
		setSelectedStudent(null);
		setSelectedStudents([]);
		setSelectedPeople([]);
		setStudentOptions([]);
		setResponsibleOptions([]);
		setGeneralOptions([]);
		setSelectedClasses([]);
		setClassOptions([]);
		setAttendanceForm(ATTENDANCE_DEFAULTS);
		setPeriodErrorMessage("");
		setStudentErrorMessage("");
		setClassErrorMessage("");
		setAttendanceErrorMessage("");
		hasShownStudentLoadErrorRef.current = false;
		hasShownClassLoadErrorRef.current = false;
		hasShownResponsibleLoadErrorRef.current = false;
		hasShownGeneralLoadErrorRef.current = false;
	};

	const toDateOnly = (isoValue: string) => {
		return dayjs(isoValue).format("YYYY-MM-DD");
	};

	const toDayjsTime = (timeValue: string): Dayjs | null => {
		if (!timeValue) return null;

		const [hourValue, minuteValue] = timeValue.split(":");
		const hour = Number(hourValue);
		const minute = Number(minuteValue);

		if (
			Number.isNaN(hour) ||
			Number.isNaN(minute) ||
			hour < 0 ||
			hour > 23 ||
			minute < 0 ||
			minute > 59
		) {
			return null;
		}

		return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
	};

	const formatTimeToString = (timeValue: Dayjs | null): string => {
		if (!timeValue || !timeValue.isValid()) return "";
		return timeValue.format("HH:mm");
	};

	const handleGenerateReport = async () => {
		setPeriodErrorMessage("");
		setStudentErrorMessage("");
		setClassErrorMessage("");
		setAttendanceErrorMessage("");

		if (requiresPeriod && !usesLast30DaysPeriod && (!startDate || !endDate)) {
			const message = "Por favor, selecione um período válido.";
			setPeriodErrorMessage(message);
			showSnackbar(message, "warning");
			return;
		}

		if (requiresStudent && !allowEmptyStudentSelection && !selectedStudent) {
			const message = "Selecione um aluno para gerar este relatório.";
			setStudentErrorMessage(message);
			showSnackbar(message, "warning");
			return;
		}

		if (isAttendanceReport) {
			if (
				!attendanceForm.entryWindowStart ||
				!attendanceForm.entryWindowEnd ||
				!attendanceForm.minimumExitTime
			) {
				const message =
					"Preencha a janela de entrada e o horário mínimo de saída.";
				setAttendanceErrorMessage(message);
				showSnackbar(message, "warning");
				return;
			}

			if (
				attendanceForm.toleranceBeforeMinutes < 0 ||
				attendanceForm.toleranceAfterMinutes < 0
			) {
				const message = "As tolerâncias devem ser maiores ou iguais a zero.";
				setAttendanceErrorMessage(message);
				showSnackbar(message, "warning");
				return;
			}
		}

		if (!selectedReportMeta) return;

		const normalizeTime = (value: string): string => {
			const normalized = value.trim();
			return normalized.length === 5 ? `${normalized}:00` : normalized;
		};

		const effectiveStartDate =
			requiresPeriod && usesLast30DaysPeriod
				? serializeDateTimeForApi(dayjs().subtract(30, "day").startOf("day"))
				: startDate;
		const effectiveEndDate =
			requiresPeriod && usesLast30DaysPeriod
				? serializeDateTimeForApi(dayjs().endOf("day"))
				: endDate;

		const selectedStudentIds = selectedPeople
			.filter((person) => person.entityType === "student")
			.map((person) => person.id);
		const selectedResponsibleIds = selectedPeople
			.filter((person) => person.entityType === "responsible")
			.map((person) => person.id);
		const selectedGeneralIds = selectedPeople
			.filter((person) => person.entityType === "general")
			.map((person) => person.id);

		const entityTypesFromSpecificFilters: EventEntityTypeOption[] = [
			...(selectedStudentIds.length > 0
				? (["student"] as EventEntityTypeOption[])
				: []),
			...(selectedResponsibleIds.length > 0
				? (["responsible"] as EventEntityTypeOption[])
				: []),
			...(selectedGeneralIds.length > 0
				? (["general"] as EventEntityTypeOption[])
				: []),
		];

		const normalizedEventEntityTypes =
			selectedReportMeta.supportsEventEntityTypeFilter
				? eventEntityTypeFilters.length === 0
					? entityTypesFromSpecificFilters.length > 0
						? Array.from(new Set(entityTypesFromSpecificFilters))
						: undefined
					: eventEntityTypeFilters.length === 4
						? undefined
						: eventEntityTypeFilters
				: undefined;

		const asyncType = selectedReportMeta.asyncReportType;
		const isAllEvents =
			selectedReportMeta.supportsOptionalPeriod && !usePeriod;

		const resolvedReportType =
			asyncType === "faces"
				? isAllEvents
					? "faces_all"
					: "faces"
				: asyncType === "device"
					? isAllEvents
						? "device_all"
						: "device"
					: asyncType;

		const noDatePeriodTypes = [
			"student_attendance",
			"student_entries_last30_days",
			"students_by_class",
		];

		const payload: IAsyncReportCreateRequest = {
			reportType: resolvedReportType,
			exportType: type,
			startTime:
				!noDatePeriodTypes.includes(asyncType) && requiresPeriod
					? effectiveStartDate
					: undefined,
			endTime:
				!noDatePeriodTypes.includes(asyncType) && requiresPeriod
					? effectiveEndDate
					: undefined,
			startDate:
				asyncType === "student_attendance"
					? toDateOnly(effectiveStartDate)
					: undefined,
			endDate:
				asyncType === "student_attendance"
					? toDateOnly(effectiveEndDate)
					: undefined,
			allEvents: isAllEvents || undefined,
			alunoId:
				asyncType === "student_entries_last30_days"
					? (selectedStudent?.id ?? undefined)
					: undefined,
			alunoIds:
				asyncType === "student_attendance"
					? shouldShowStudentFilter
						? selectedStudents.map((s) => s.id)
						: []
					: selectedReportMeta.supportsEventEntityTypeFilter
						? hasStudentSelectedInEventFilter
							? selectedStudentIds
							: undefined
						: selectedReportMeta.supportsStudentFilter
							? selectedStudents.map((s) => s.id)
							: undefined,
			responsavelIds:
				selectedReportMeta.supportsEventEntityTypeFilter &&
				hasResponsibleSelectedInEventFilter
					? selectedResponsibleIds
					: undefined,
			generalIds:
				selectedReportMeta.supportsEventEntityTypeFilter &&
				hasGeneralSelectedInEventFilter
					? selectedGeneralIds
					: undefined,
			schoolClassIds:
				asyncType === "student_attendance"
					? shouldShowClassesFilter
						? selectedClasses.map((c) => c.id)
						: []
					: (selectedReportMeta.supportsClassesFilter ||
							selectedReportMeta.requiresClasses) &&
						hasStudentSelectedInEventFilter
						? selectedClasses.map((c) => c.id)
						: undefined,
			eventEntityTypes: normalizedEventEntityTypes,
			exitEntityTypes:
				selectedReportMeta.supportsExitEntityTypeFilter &&
				exitEntityTypeFilters.length > 0
					? exitEntityTypeFilters
					: undefined,
			discriminators:
				selectedReportMeta.supportsDiscriminatorFilter &&
				recognitionTypeFilters.length > 0
					? recognitionTypeFilters
					: undefined,
			includeImages: selectedReportMeta.supportsIncludeImages
				? includeImages
				: undefined,
			includeDevice: selectedReportMeta.supportsIncludeDeviceOption
				? includeDevice
				: undefined,
			includeIp: selectedReportMeta.supportsIncludeIpOption
				? includeIp
				: undefined,
			includeSchoolClass:
				selectedReportMeta.supportsIncludeSchoolClassOption &&
				hasStudentSelectedInEventFilter
					? includeSchoolClass
					: undefined,
			includePersonType: selectedReportMeta.supportsIncludePersonTypeOption
				? includePersonType
				: undefined,
			includeCreatedAt: selectedReportMeta.supportsIncludeCreatedAtOption
				? includeCreatedAt
				: undefined,
			includeResponsibleUser:
				selectedReportMeta.supportsIncludeResponsibleUserOption
					? includeResponsibleUser
					: undefined,
			onlyEntry:
				asyncType === "student_attendance"
					? attendanceForm.evaluationMode === "only_entry"
					: undefined,
			entryWindowStart:
				asyncType === "student_attendance"
					? normalizeTime(attendanceForm.entryWindowStart)
					: undefined,
			entryWindowEnd:
				asyncType === "student_attendance"
					? normalizeTime(attendanceForm.entryWindowEnd)
					: undefined,
			toleranceBeforeMinutes:
				asyncType === "student_attendance"
					? attendanceForm.toleranceBeforeMinutes
					: undefined,
			toleranceAfterMinutes:
				asyncType === "student_attendance"
					? attendanceForm.toleranceAfterMinutes
					: undefined,
			minimumExitTime:
				asyncType === "student_attendance" &&
				attendanceForm.evaluationMode !== "only_entry"
					? normalizeTime(attendanceForm.minimumExitTime)
					: undefined,
		};

		setIsSubmitting(true);
		try {
			const response = await createAsyncReport(payload);
			activeReportNameRef.current = selectedReportMeta.reportName;
			setIsDialogOpen(false);
			jobProgress.start(response.jobId, selectedReportMeta.reportName, (msg) => {
				showSnackbar(msg || "Nenhum registro encontrado no período.", "warning");
				setIsDialogOpen(true);
			});
		} catch (error: unknown) {
			const msg =
				error instanceof Error
					? error.message
					: "Erro ao enfileirar relatório.";
			showSnackbar(msg, "error");
		} finally {
			setIsSubmitting(false);
		}
	};

	const columns = [
		{ id: "reportName", label: "Relatório", width: "50%" },
		{
			id: "group",
			label: "Grupo",
			width: "25%",
			render: (_groupValue: string, _rowIndex: number, row?: ReportRow) => {
				const groups = row?.group ?? [];

				return (
					<Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
						{groups.map((group) => (
							<Chip
								key={group}
								size="small"
								label={group}
								variant="outlined"
								sx={(theme) => {
									const tone = groupColorMap[group];

									if (tone === "neutral") {
										return {
											fontWeight: 600,
											borderColor: theme.palette.grey[600],
											color: theme.palette.grey[300],
											backgroundColor: alpha(theme.palette.grey[500], 0.12),
										};
									}

									return {
										fontWeight: 600,
										borderColor: theme.palette[tone].main,
										color: theme.palette[tone].light,
										backgroundColor: alpha(theme.palette[tone].main, 0.16),
									};
								}}
							/>
						))}
					</Box>
				);
			},
		},
		{
			id: "id",
			label: "Ação",
			width: "25%",
			render: (id: string) => (
				<Button
					variant="contained"
					color="primary"
					onClick={() => handleOpenDialog(Number(id))}
					sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
				>
					Gerar
				</Button>
			),
		},
	];

	const normalizeDateToIso = (
		value: unknown,
		boundary: "start" | "end",
		pickerType: DatePickerType,
	) => {
		if (!value) return "";
		const parsed = dayjs(value as string | number | Date | Dayjs);
		if (!parsed.isValid()) return "";

		if (pickerType === "date") {
			if (boundary === "start") {
				return serializeDateTimeForApi(parsed.startOf("day"));
			}

			return serializeDateTimeForApi(
				parsed.hour(23).minute(59).second(0).millisecond(0),
			);
		}

		return serializeDateTimeForApi(parsed);
	};

	const isGenerateDisabled =
		isSubmitting ||
		jobProgress.isActive ||
		(requiresStudent && !allowEmptyStudentSelection && !selectedStudent) ||
		(isAttendanceReport &&
			(attendanceForm.toleranceBeforeMinutes < 0 ||
				attendanceForm.toleranceAfterMinutes < 0)) ||
		(requiresPeriod && !usesLast30DaysPeriod && (!startDate || !endDate));

	const sortedReports = useMemo<ReportRowView[]>(
		() =>
			[...reports]
				.sort((a, b) =>
					a.reportName.localeCompare(b.reportName, "pt-BR", {
						sensitivity: "base",
					}),
				)
				.map((report, index) => ({ ...report, sortOrder: index })),
		[reports],
	);

	const filteredReports = useMemo(
		() =>
			sortedReports.filter((report) => {
				const normalizedNameFilter = reportNameFilter.trim().toLowerCase();
				const matchesName =
					!normalizedNameFilter ||
					report.reportName.toLowerCase().includes(normalizedNameFilter);
				const matchesGroup =
					selectedGroupFilters.length === 0 ||
					report.group.some((group) => selectedGroupFilters.includes(group));

				return matchesName && matchesGroup;
			}),
		[reportNameFilter, selectedGroupFilters, sortedReports],
	);

	const resetPaginationToFirstPage = () => {
		setPagination((previous) =>
			previous.pageNumber === 1
				? previous
				: {
						...previous,
						pageNumber: 1,
					},
		);
	};

	const handleMainFilterChange = (value: string) => {
		setReportNameFilter(value);
		resetPaginationToFirstPage();
	};

	const handleGroupFilterChange = (value: ReportRow["group"][number][]) => {
		setSelectedGroupFilters(value);
		resetPaginationToFirstPage();
	};

	const paginatedReports = useMemo(
		() =>
			filteredReports.slice(
				(pagination.pageNumber - 1) * pagination.pageSize,
				pagination.pageNumber * pagination.pageSize,
			),
		[filteredReports, pagination],
	);

	return (
		<Box
			sx={{
				width: "100%",
				p: 0,
				backgroundColor: "transparent",
			}}
		>
			<TableComponent
				columns={columns}
				totalCount={filteredReports.length}
				rows={paginatedReports}
				onChangePageOrRowsPerPage={setPagination}
				onChangeMainFilter={handleMainFilterChange}
				searchValue={reportNameFilter}
				sortByName={"sortOrder"}
				defaultOrder="asc"
				title="Central de Relatórios"
				currentPageNumber={pagination.pageNumber}
				additionalTitleComponent={
					<Box sx={{ minWidth: { xs: 180, sm: 240 } }}>
						<FormControl fullWidth size="small">
							<InputLabel id="report-group-filter-label">Grupo</InputLabel>
							<Select
								labelId="report-group-filter-label"
								multiple
								value={selectedGroupFilters}
								label="Grupo"
								onChange={(event) => {
									const value = event.target.value;
									handleGroupFilterChange(
										typeof value === "string"
											? (value.split(",") as ReportRow["group"][number][])
											: (value as ReportRow["group"][number][]),
									);
								}}
								renderValue={(selected) =>
									selected.length === 0
										? "Todos os grupos"
										: selected.join(", ")
								}
							>
								{availableGroups.map((group) => (
									<MenuItem key={group} value={group}>
										<Checkbox
											checked={selectedGroupFilters.includes(group)}
											sx={{ mr: 1 }}
										/>
										{group}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
				}
			/>

			<ProgressDialog
				open={jobProgress.isActive || jobProgress.status !== "loading"}
				onClose={jobProgress.reset}
				onCancel={() => {
					void jobProgress.cancel();
				}}
				progress={jobProgress.progress}
				status={jobProgress.status}
				statusMessage={jobProgress.statusMessage}
				title={activeReportNameRef.current ?? "Relatório"}
				etaSeconds={jobProgress.etaSeconds}
			/>

			<Dialog
				open={isDialogOpen}
				onClose={handleCloseDialog}
				fullWidth
				maxWidth="sm"
				slotProps={{
					paper: {
						sx: {
							borderRadius: 2,
							overflow: "hidden",
						},
					},
				}}
			>
				<DialogTitle
					component="div"
					sx={{
						pb: 1.25,
						borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
					}}
				>
					<Typography variant="h6" fontWeight={700}>
						Gerar relatório
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
						{selectedReportMeta?.reportName ??
							"Selecione os parâmetros de geração"}
					</Typography>
				</DialogTitle>
				<DialogContent sx={{ pt: 2, pb: 1.5 }}>
					<Grid container spacing={2}>
						{supportsOptionalPeriod && (
							<Grid item xs={12}>
								<Box
									sx={{
										display: "flex",
										alignItems: "flex-start",
										gap: 0.5,
										px: 1,
										py: 0.75,
										border: (theme) =>
											`1px solid ${alpha(theme.palette.info.main, 0.28)}`,
										borderRadius: 1.5,
										backgroundColor: (theme) =>
											alpha(theme.palette.info.main, 0.08),
									}}
								>
									<Checkbox
										checked={usePeriod}
										onChange={(e) => {
											const checked = e.target.checked;
											setUsePeriod(checked);
											setPeriodErrorMessage("");
											if (checked) {
												const suggestedPeriod = getSuggestedPeriodRange();
												setStartDate(suggestedPeriod.start);
												setEndDate(suggestedPeriod.end);
												return;
											}

											setStartDate("");
											setEndDate("");
										}}
										sx={{ mt: -0.25 }}
									/>
									<Box sx={{ pt: 0.25 }}>
										<Typography variant="body2" fontWeight={600}>
											Usar período
										</Typography>
										<Typography variant="caption" color="text.secondary">
											Desmarque para gerar o relatório completo, sem filtro de
											datas.
										</Typography>
									</Box>
								</Box>
							</Grid>
						)}

						{usesLast30DaysPeriod ? (
							<Grid item xs={12}>
								<Box
									sx={{
										px: 1.5,
										py: 1,
										borderRadius: 1.5,
										backgroundColor: (theme) =>
											alpha(theme.palette.info.main, 0.12),
										border: (theme) =>
											`1px solid ${alpha(theme.palette.info.main, 0.35)}`,
									}}
								>
									<Typography variant="body2">
										Este relatório usa período fixo dos últimos 30 dias.
									</Typography>
								</Box>
							</Grid>
						) : requiresPeriod ? (
							<>
								<Grid item xs={12} sm={6}>
									<Box sx={{ mt: 1 }}>
										<DatePickerComponent
											type={selectedPeriodType}
											value={startDate ? dayjs(startDate) : null}
											onChange={(val) =>
												setStartDate(
													normalizeDateToIso(val, "start", selectedPeriodType),
												)
											}
											label="Início"
											errorMessage={periodErrorMessage || undefined}
											disableFuture={selectedPeriodType === "date"}
										/>
									</Box>
								</Grid>

								<Grid item xs={12} sm={6}>
									<Box sx={{ mt: 1 }}>
										<DatePickerComponent
											type={selectedPeriodType}
											value={endDate ? dayjs(endDate) : null}
											onChange={(val) =>
												setEndDate(
													normalizeDateToIso(val, "end", selectedPeriodType),
												)
											}
											label="Fim"
											errorMessage={periodErrorMessage || undefined}
											disableFuture={selectedPeriodType === "date"}
										/>
									</Box>
								</Grid>
							</>
						) : (
							<Grid item xs={12}>
								<Box
									sx={{
										px: 1.5,
										py: 1,
										borderRadius: 1.5,
										backgroundColor: (theme) =>
											alpha(theme.palette.info.main, 0.12),
										border: (theme) =>
											`1px solid ${alpha(theme.palette.info.main, 0.35)}`,
									}}
								>
									<Typography variant="body2">
										Este relatório não utiliza filtro de período.
									</Typography>
								</Box>
							</Grid>
						)}

						{isAttendanceReport && (
							<>
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth size="small">
										<InputLabel id="attendance-evaluation-mode-label">
											Critério de presença
										</InputLabel>
										<Select
											labelId="attendance-evaluation-mode-label"
											label="Critério de presença"
											value={attendanceForm.evaluationMode}
											onChange={(event) =>
												setAttendanceForm((prev) => ({
													...prev,
													evaluationMode: event.target.value as
														| "entry_and_exit"
														| "only_entry",
												}))
											}
										>
											<MenuItem value="entry_and_exit">
												Entrada e saída
											</MenuItem>
											<MenuItem value="only_entry">Somente entrada</MenuItem>
										</Select>
									</FormControl>
								</Grid>

								<Grid item xs={12} sm={6}>
									<TimePicker
										label="Janela de entrada (início)"
										value={toDayjsTime(attendanceForm.entryWindowStart)}
										onChange={(value) =>
											setAttendanceForm((prev) => ({
												...prev,
												entryWindowStart: formatTimeToString(value),
											}))
										}
										ampm={false}
										format="HH:mm"
										slotProps={{
											textField: {
												fullWidth: true,
												size: "small",
												error: !!attendanceErrorMessage,
											},
											openPickerButton: { color: "primary" },
										}}
									/>
								</Grid>

								<Grid item xs={12} sm={6}>
									<TimePicker
										label="Janela de entrada (fim)"
										value={toDayjsTime(attendanceForm.entryWindowEnd)}
										onChange={(value) =>
											setAttendanceForm((prev) => ({
												...prev,
												entryWindowEnd: formatTimeToString(value),
											}))
										}
										ampm={false}
										format="HH:mm"
										slotProps={{
											textField: {
												fullWidth: true,
												size: "small",
												error: !!attendanceErrorMessage,
											},
											openPickerButton: { color: "primary" },
										}}
									/>
								</Grid>

								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										size="small"
										type="number"
										label="Tolerância antes (min)"
										value={attendanceForm.toleranceBeforeMinutes}
										onChange={(event) =>
											setAttendanceForm((prev) => ({
												...prev,
												toleranceBeforeMinutes: Number(event.target.value || 0),
											}))
										}
										inputProps={{ min: 0 }}
									/>
								</Grid>

								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										size="small"
										type="number"
										label="Tolerância depois (min)"
										value={attendanceForm.toleranceAfterMinutes}
										onChange={(event) =>
											setAttendanceForm((prev) => ({
												...prev,
												toleranceAfterMinutes: Number(event.target.value || 0),
											}))
										}
										inputProps={{ min: 0 }}
									/>
								</Grid>

								<Grid item xs={12} sm={6}>
									<TimePicker
										label="Saída mínima"
										value={toDayjsTime(attendanceForm.minimumExitTime)}
										disabled={attendanceForm.evaluationMode === "only_entry"}
										onChange={(value) =>
											setAttendanceForm((prev) => ({
												...prev,
												minimumExitTime: formatTimeToString(value),
											}))
										}
										ampm={false}
										format="HH:mm"
										slotProps={{
											textField: {
												fullWidth: true,
												size: "small",
												error:
													attendanceForm.evaluationMode !== "only_entry" &&
													!!attendanceErrorMessage,
												helperText:
													attendanceForm.evaluationMode === "only_entry"
														? "Não aplicado no critério Somente entrada."
														: attendanceErrorMessage || undefined,
											},
											openPickerButton: { color: "primary" },
										}}
									/>
								</Grid>
							</>
						)}

						{requiresStudent && (
							<Grid item xs={12}>
								<Autocomplete
									options={studentOptions}
									value={selectedStudent}
									onChange={(_, value) => setSelectedStudent(value)}
									inputValue={studentQuery}
									onInputChange={(_, value) => setStudentQuery(value)}
									loading={isStudentLoading}
									isOptionEqualToValue={(option, value) =>
										option.id === value.id
									}
									getOptionLabel={(option) => option.name}
									noOptionsText="Nenhum aluno encontrado"
									filterOptions={(options, state) => {
										return filterStudentOptionsByQuery(
											options,
											state.inputValue,
										);
									}}
									renderOption={(props, option) => (
										<Box
											component="li"
											{...props}
											key={`${option.id ?? "no-id"}-${option.registration ?? "no-reg"}-${option.cpf ?? "no-cpf"}-${option.name}`}
											sx={{ py: 0.75 }}
										>
											<Box>
												<Typography variant="body2" fontWeight={600}>
													{option.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{getStudentIdentifier(option)}
												</Typography>
											</Box>
										</Box>
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											label={
												allowEmptyStudentSelection
													? "Aluno (opcional)"
													: "Aluno"
											}
											placeholder="Digite nome, matrícula ou CPF"
											error={!!studentErrorMessage}
											helperText={
												studentErrorMessage ||
												(allowEmptyStudentSelection
													? "Se nenhum aluno for selecionado, o relatório será gerado para todos."
													: undefined)
											}
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isStudentLoading ? (
															<CircularProgress color="inherit" size={18} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</Grid>
						)}

						{shouldShowStudentOnlyFilter && (
							<Grid item xs={12}>
								<Autocomplete
									multiple
									disableCloseOnSelect
									options={studentOptions}
									value={selectedStudents}
									onChange={(_, value) => setSelectedStudents(value)}
									inputValue={studentQuery}
									onInputChange={(_, value) => setStudentQuery(value)}
									loading={isStudentLoading}
									isOptionEqualToValue={(option, value) =>
										option.id === value.id
									}
									getOptionLabel={(option) => option.name}
									noOptionsText="Nenhum aluno encontrado"
									filterOptions={(options, state) =>
										filterStudentOptionsByQuery(options, state.inputValue)
									}
									renderOption={(props, option, { selected }) => (
										<Box component="li" {...props} key={option.id}>
											<Checkbox checked={selected} sx={{ mr: 1 }} />
											<Box>
												<Typography variant="body2" fontWeight={600}>
													{option.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{getStudentIdentifier(option)}
												</Typography>
											</Box>
										</Box>
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Alunos (opcional)"
											placeholder="Filtre por nome, matrícula ou CPF"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isStudentLoading ? (
															<CircularProgress color="inherit" size={18} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</Grid>
						)}

						{shouldShowUnifiedPeopleFilter && (
							<Grid item xs={12}>
								<Autocomplete
									multiple
									disableCloseOnSelect
									options={peopleOptions}
									value={selectedPeople}
									onChange={(_, value) => setSelectedPeople(value)}
									inputValue={studentQuery}
									onInputChange={(_, value) => setStudentQuery(value)}
									loading={isPeopleLoading}
									isOptionEqualToValue={(option, value) =>
										option.id === value.id &&
										option.entityType === value.entityType
									}
									getOptionLabel={(option) => option.name}
									noOptionsText="Nenhuma pessoa encontrada"
									filterOptions={(options, state) =>
										filterPersonOptionsByQuery(options, state.inputValue)
									}
									renderOption={(props, option, { selected }) => (
										<Box
											component="li"
											{...props}
											key={`${option.entityType}-${option.id}`}
										>
											<Checkbox checked={selected} sx={{ mr: 1 }} />
											<Box>
												<Typography variant="body2" fontWeight={600}>
													{option.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{`${formatEntityTypeLabel(option.entityType)} • ${getPersonIdentifier(option)}`}
												</Typography>
											</Box>
										</Box>
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Pessoas (opcional)"
											placeholder="Filtre por nome, matrícula, CPF ou tipo"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isPeopleLoading ? (
															<CircularProgress color="inherit" size={18} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</Grid>
						)}

						{shouldShowClassesFilter && (
							<Grid item xs={12}>
								<Autocomplete
									multiple
									disableCloseOnSelect
									options={classOptions}
									value={selectedClasses}
									onChange={(_, value) => setSelectedClasses(value)}
									loading={isClassLoading}
									isOptionEqualToValue={(option, value) =>
										option.id === value.id
									}
									getOptionLabel={(option) => option.name}
									renderOption={(props, option, { selected }) => (
										<Box component="li" {...props} key={option.id}>
											<Checkbox checked={selected} sx={{ mr: 1 }} />
											{option.name}
										</Box>
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Turmas (opcional)"
											placeholder="Selecione uma ou mais turmas"
											error={!!classErrorMessage}
											helperText={
												classErrorMessage ||
												"Se nenhuma for selecionada, todas as turmas serão consideradas."
											}
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isClassLoading ? (
															<CircularProgress color="inherit" size={18} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</Grid>
						)}

						<Grid item xs={12}>
							<FormControl fullWidth size="small">
								<InputLabel id="report-type-label">Formato</InputLabel>
								<Select
									labelId="report-type-label"
									value={type}
									label="Formato"
									onChange={(e) => setType(e.target.value)}
								>
									<MenuItem value="pdf">PDF</MenuItem>
									<MenuItem value="excel">Excel</MenuItem>
								</Select>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ mt: 0.5, display: "block" }}
								>
									Selecione o formato do arquivo.
								</Typography>
							</FormControl>
						</Grid>

						{supportsDiscriminatorFilter && (
							<Grid item xs={12}>
								<FormControl fullWidth size="small">
									<InputLabel id="recognition-type-filter-label">
										Tipo de reconhecimento
									</InputLabel>
									<Select
										labelId="recognition-type-filter-label"
										multiple
										value={recognitionTypeFilters}
										label="Tipo de reconhecimento"
										onChange={(event) => {
											const value = event.target.value;
											setRecognitionTypeFilters(
												typeof value === "string" ? value.split(",") : value,
											);
										}}
										renderValue={(selected) => {
											if (!selected || selected.length === 0) {
												return "Todos";
											}

											const labels: Record<string, string> = {
												student: "Estudante",
												responsible: "Responsável",
												general: "Funcionário",
											};

											return selected
												.map((item) => labels[item] ?? item)
												.join(", ");
										}}
									>
										<MenuItem value="student">
											<Checkbox
												checked={recognitionTypeFilters.includes("student")}
												sx={{ mr: 1 }}
											/>
											Estudante
										</MenuItem>
										<MenuItem value="responsible">
											<Checkbox
												checked={recognitionTypeFilters.includes("responsible")}
												sx={{ mr: 1 }}
											/>
											Responsável
										</MenuItem>
										<MenuItem value="general">
											<Checkbox
												checked={recognitionTypeFilters.includes("general")}
												sx={{ mr: 1 }}
											/>
											Funcionário
										</MenuItem>
									</Select>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mt: 0.5, display: "block" }}
									>
										Deixe sem seleção para incluir todos os tipos.
									</Typography>
								</FormControl>
							</Grid>
						)}

						{supportsEventEntityTypeFilter && (
							<Grid item xs={12}>
								<FormControl fullWidth size="small">
									<InputLabel id="event-entity-type-filter-label">
										Tipos
									</InputLabel>
									<Select
										labelId="event-entity-type-filter-label"
										multiple
										value={eventEntityTypeFilters}
										label="Tipos"
										onChange={(event) => {
											const value = event.target.value;
											setEventEntityTypeFilters(
												typeof value === "string"
													? (value.split(",") as EventEntityTypeOption[])
													: (value as EventEntityTypeOption[]),
											);
										}}
										renderValue={(selected) => {
											if (!selected || selected.length === 0) {
												return "Todos os tipos";
											}

											const labels: Record<EventEntityTypeOption, string> = {
												student: "Estudante",
												responsible: "Responsável",
												general: "Funcionário",
												unregistered: "Não cadastrado",
											};

											return selected
												.map(
													(item) =>
														labels[item as EventEntityTypeOption] ?? item,
												)
												.join(", ");
										}}
									>
										<MenuItem value="student">
											<Checkbox
												checked={eventEntityTypeFilters.includes("student")}
												sx={{ mr: 1 }}
											/>
											Estudante
										</MenuItem>
										<MenuItem value="responsible">
											<Checkbox
												checked={eventEntityTypeFilters.includes("responsible")}
												sx={{ mr: 1 }}
											/>
											Responsável
										</MenuItem>
										<MenuItem value="general">
											<Checkbox
												checked={eventEntityTypeFilters.includes("general")}
												sx={{ mr: 1 }}
											/>
											Funcionário
										</MenuItem>
										<MenuItem value="unregistered">
											<Checkbox
												checked={eventEntityTypeFilters.includes(
													"unregistered",
												)}
												sx={{ mr: 1 }}
											/>
											Não cadastrado
										</MenuItem>
									</Select>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mt: 0.5, display: "block" }}
									>
										Quando Estudante estiver selecionado, os filtros de turma
										ficam disponíveis.
									</Typography>
								</FormControl>
							</Grid>
						)}

						{supportsExitEntityTypeFilter && (
							<Grid item xs={12}>
								<FormControl fullWidth size="small">
									<InputLabel id="exit-entity-type-filter-label">
										Tipos
									</InputLabel>
									<Select
										labelId="exit-entity-type-filter-label"
										multiple
										value={exitEntityTypeFilters}
										label="Tipos"
										onChange={(event) => {
											const value = event.target.value;
											setExitEntityTypeFilters(
												typeof value === "string"
													? (value.split(",") as ExitEntityTypeOption[])
													: (value as ExitEntityTypeOption[]),
											);
										}}
										renderValue={(selected) => {
											if (!selected || selected.length === 0) {
												return "Todos os tipos";
											}

											const labels: Record<ExitEntityTypeOption, string> = {
												student: "Estudante",
												responsible: "Responsável",
												general: "Geral",
											};

											return selected
												.map(
													(item) =>
														labels[item as ExitEntityTypeOption] ?? item,
												)
												.join(", ");
										}}
									>
										<MenuItem value="student">
											<Checkbox
												checked={exitEntityTypeFilters.includes("student")}
												sx={{ mr: 1 }}
											/>
											Estudante
										</MenuItem>
										<MenuItem value="responsible">
											<Checkbox
												checked={exitEntityTypeFilters.includes("responsible")}
												sx={{ mr: 1 }}
											/>
											Responsável
										</MenuItem>
										<MenuItem value="general">
											<Checkbox
												checked={exitEntityTypeFilters.includes("general")}
												sx={{ mr: 1 }}
											/>
											Geral
										</MenuItem>
									</Select>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mt: 0.5, display: "block" }}
									>
										Deixe sem seleção para gerar todos os tipos.
									</Typography>
								</FormControl>
							</Grid>
						)}

						{hasAnyColumnOption && (
							<Grid item xs={12}>
								<Box sx={{ px: 0.5 }}>
									<Typography variant="subtitle2" sx={{ mb: 0.75 }}>
										Campos do relatório
									</Typography>

									<Box
										sx={{
											border: (theme) =>
												`1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
											borderRadius: 1.5,
											overflow: "hidden",
											backgroundColor: (theme) =>
												alpha(theme.palette.primary.main, 0.06),
										}}
									>
										{supportsIncludeImagesOption && (
											<Box
												sx={{
													display: "flex",
													alignItems: "flex-start",
													gap: 0.5,
													px: 1,
													py: 0.75,
													borderBottom:
														supportsIncludeCreatedAtOption ||
														supportsIncludeResponsibleUserOption ||
														supportsIncludeSchoolClassOption ||
														supportsIncludeDeviceOption ||
														supportsIncludeIpOption ||
														supportsIncludePersonTypeOption
															? (theme) =>
																	`1px solid ${alpha(theme.palette.primary.main, 0.12)}`
															: "none",
												}}
											>
												<Checkbox
													checked={includeImages}
													onChange={(e) => setIncludeImages(e.target.checked)}
													sx={{ mt: -0.25 }}
												/>
												<Box sx={{ pt: 0.25 }}>
													<Typography variant="body2" fontWeight={600}>
														Imagem da pessoa
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Inclui miniatura da face no arquivo.
													</Typography>
												</Box>
											</Box>
										)}

										{supportsIncludePersonTypeOption && (
											<Box
												sx={{
													display: "flex",
													alignItems: "flex-start",
													gap: 0.5,
													px: 1,
													py: 0.75,
													borderBottom:
														supportsIncludeCreatedAtOption ||
														supportsIncludeResponsibleUserOption ||
														supportsIncludeIpOption ||
														supportsIncludeSchoolClassOption
															? (theme) =>
																	`1px solid ${alpha(theme.palette.primary.main, 0.12)}`
															: "none",
												}}
											>
												<Checkbox
													checked={includePersonType}
													onChange={(e) =>
														setIncludePersonType(e.target.checked)
													}
													sx={{ mt: -0.25 }}
												/>
												<Box sx={{ pt: 0.25 }}>
													<Typography variant="body2" fontWeight={600}>
														Tipo de pessoa
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Exibe se o evento é de Estudante, Responsável ou
														Geral.
													</Typography>
												</Box>
											</Box>
										)}

										{supportsIncludeDeviceOption && (
											<Box
												sx={{
													display: "flex",
													alignItems: "flex-start",
													gap: 0.5,
													px: 1,
													py: 0.75,
													borderBottom:
														supportsIncludeCreatedAtOption ||
														supportsIncludeResponsibleUserOption ||
														supportsIncludeIpOption ||
														shouldShowIncludeSchoolClassOption
															? (theme) =>
																	`1px solid ${alpha(theme.palette.primary.main, 0.12)}`
															: "none",
												}}
											>
												<Checkbox
													checked={includeDevice}
													onChange={(e) => setIncludeDevice(e.target.checked)}
													sx={{ mt: -0.25 }}
												/>
												<Box sx={{ pt: 0.25 }}>
													<Typography variant="body2" fontWeight={600}>
														Dispositivo
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Exibe o equipamento que registrou o evento.
													</Typography>
												</Box>
											</Box>
										)}

										{supportsIncludeIpOption && (
											<Box
												sx={{
													display: "flex",
													alignItems: "flex-start",
													gap: 0.5,
													px: 1,
													py: 0.75,
													borderBottom:
														supportsIncludeCreatedAtOption ||
														supportsIncludeResponsibleUserOption ||
														shouldShowIncludeSchoolClassOption
															? (theme) =>
																	`1px solid ${alpha(theme.palette.primary.main, 0.12)}`
															: "none",
												}}
											>
												<Checkbox
													checked={includeIp}
													onChange={(e) => setIncludeIp(e.target.checked)}
													sx={{ mt: -0.25 }}
												/>
												<Box sx={{ pt: 0.25 }}>
													<Typography variant="body2" fontWeight={600}>
														IP
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Exibe o endereço IP do dispositivo no relatório.
													</Typography>
												</Box>
											</Box>
										)}

										{shouldShowIncludeSchoolClassOption && (
											<Box
												sx={{
													display: "flex",
													alignItems: "flex-start",
													gap: 0.5,
													px: 1,
													py: 0.75,
													borderBottom:
														supportsIncludeCreatedAtOption ||
														supportsIncludeResponsibleUserOption
															? (theme) =>
																	`1px solid ${alpha(theme.palette.primary.main, 0.12)}`
															: "none",
												}}
											>
												<Checkbox
													checked={includeSchoolClass}
													onChange={(e) =>
														setIncludeSchoolClass(e.target.checked)
													}
													sx={{ mt: -0.25 }}
												/>
												<Box sx={{ pt: 0.25 }}>
													<Typography variant="body2" fontWeight={600}>
														Turma
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Exibe a turma vinculada ao aluno no evento.
													</Typography>
												</Box>
											</Box>
										)}

										{supportsIncludeCreatedAtOption && (
											<Box
												sx={{
													display: "flex",
													alignItems: "flex-start",
													gap: 0.5,
													px: 1,
													py: 0.75,
													borderBottom: supportsIncludeResponsibleUserOption
														? (theme) =>
																`1px solid ${alpha(theme.palette.primary.main, 0.12)}`
														: "none",
												}}
											>
												<Checkbox
													checked={includeCreatedAt}
													onChange={(e) =>
														setIncludeCreatedAt(e.target.checked)
													}
													sx={{ mt: -0.25 }}
												/>
												<Box sx={{ pt: 0.25 }}>
													<Typography variant="body2" fontWeight={600}>
														Criado em
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Mostra a data de criação do cadastro.
													</Typography>
												</Box>
											</Box>
										)}

										{supportsIncludeResponsibleUserOption && (
											<Box
												sx={{
													display: "flex",
													alignItems: "flex-start",
													gap: 0.5,
													px: 1,
													py: 0.75,
												}}
											>
												<Checkbox
													checked={includeResponsibleUser}
													onChange={(e) =>
														setIncludeResponsibleUser(e.target.checked)
													}
													sx={{ mt: -0.25 }}
												/>
												<Box sx={{ pt: 0.25 }}>
													<Typography variant="body2" fontWeight={600}>
														Usuário responsável
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Exibe quem atualizou, com perfil/cargo.
													</Typography>
												</Box>
											</Box>
										)}
									</Box>
								</Box>
							</Grid>
						)}
					</Grid>
				</DialogContent>
				<DialogActions
					sx={{
						px: 3,
						pb: 2,
						pt: 1.5,
						gap: 1,
						borderTop: (theme) => `1px solid ${theme.palette.divider}`,
					}}
				>
					<Button
						onClick={handleCloseDialog}
						color="secondary"
						variant="contained"
						sx={{ minWidth: 110 }}
					>
						Cancelar
					</Button>
					<Button
						onClick={handleGenerateReport}
						variant="contained"
						color="primary"
						sx={{ minWidth: 110 }}
						disabled={isGenerateDisabled}
					>
						Gerar
					</Button>
				</DialogActions>
			</Dialog>

		</Box>
	);
};

export default ReportTable;
